import React, { useCallback, useEffect, useState } from "react";
import Table, { Column, DataSourceItem } from "../../../../Components/Atoms/Table";
import Tag from "../../../../Components/Atoms/Tag";
import { createSortFunction } from "../../../../Utils/TableUtils";
import {
  AdyenTransactionableType,
  AdyenTransactionStatus,
  getPaymentMethodDetails,
  Transaction
} from "../../../../Contracts/Transaction";
import { toLocalDateString } from "../../../../Utils/DateUtils";
import { useTransactionStore } from "../../../../Store/Transaction";
import { useAuth0 } from "@auth0/auth0-react";
import LoadingPage from "../../../LoadingPage";
import TablePaginationFooter from "../../../../Components/Atoms/Table/TablePaginationFooter";
import { useParams } from "react-router-dom";
import { useTeamStore } from "../../../../Store/Team";
import TransactionListActions from "../../../TransactionListPage/TransactionListActions";

const CustomerDetailTransactionsList: React.FC = () => {
  const [sortParamValue, setSortParamValue] = useState<string>("created_at:desc");
  const [page, setPage] = useState<number>(1);
  const [actionsOpen, setActionsOpen] = useState<number>(0);
  const { formatCurrency, getCurrentTeam, currentTeam } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();

  const conditionalActionsColumn: Column<Transaction>[] = [
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: d => (
        <TransactionListActions
          transaction={d.data}
          open={d.data.id === actionsOpen}
          onClose={() => setActionsOpen(0)}
          onOpen={() => setActionsOpen(d.data.id)}
        />
      )
    }
  ];

  const columns: Column<Transaction>[] = [
    {
      title: "Invoice / Document",
      dataIndex: "entity",
      key: "entity",
      render: d => (
        <span>{`${
          d.data.transactionable_type === AdyenTransactionableType.CollectibleInvoice
            ? "Invoice"
            : "Document"
        } ${d.data.transaction_identifier}`}</span>
      )
    },
    {
      title: "Transaction Id",
      dataIndex: "transaction_id",
      key: "transaction_id"
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      sortClicked: createSortFunction("created_at", setSortParamValue),
      render: d => (
        <span title={toLocalDateString(d.data.created_at, true)}>
          {toLocalDateString(d.data.created_at)}
        </span>
      ),
      defaultSortOrder: "desc"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sortClicked: createSortFunction("status", setSortParamValue),
      render: d => (
        <Tag
          color={
            d.data.status === AdyenTransactionStatus.Completed
              ? "lime"
              : d.data.status === AdyenTransactionStatus.Processing
                ? "yellow"
                : d.data.status === AdyenTransactionStatus.Refunded
                  ? "gray"
                  : d.data.status === AdyenTransactionStatus.Failed ||
                      d.data.status === AdyenTransactionStatus.Chargeback
                    ? "red"
                    : d.data.status === AdyenTransactionStatus.RefundInitiated
                      ? "blue"
                      : "gray"
          }
        >
          {d.data.status}
        </Tag>
      )
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sortClicked: createSortFunction("total_amount", setSortParamValue)
    },
    {
      title: "Total",
      dataIndex: "net_amount",
      key: "net_amount",
      sortClicked: createSortFunction("net_amount", setSortParamValue)
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      render: d => {
        const imageDetails =
          d.data.payment_method && getPaymentMethodDetails(d.data.payment_method);
        return (
          imageDetails && (
            <div className="flex items-center space-x-2 w-32">
              <img src={imageDetails.logo} alt={imageDetails.name} className="h-10" />
              <div>{imageDetails.name}</div>
            </div>
          )
        );
      }
    },
    ...(currentTeam?.early_access_features?.["manual-refunds"] ? conditionalActionsColumn : [])
  ];

  const { isLoading, transactions, transactionsCount, getTransactions } = useTransactionStore();

  const localStorageData = localStorage.getItem("CUSTOMER_DETAIL_TRANSACTIONS_LIST");
  const [limit, setLimit] = useState<10 | 25 | 50 | 100>(
    localStorageData ? JSON.parse(localStorageData).limit : 25
  );

  useEffect(() => {
    localStorage.setItem(
      "CUSTOMER_DETAIL_TRANSACTIONS_LIST",
      JSON.stringify({
        limit
      })
    );
  }, [limit]);

  const { id: customerID } = useParams();

  const refreshThings = useCallback(async () => {
    await getTransactions(
      getAccessTokenSilently,
      limit,
      sortParamValue,
      page,
      `customer_id:eq:${customerID}`
    );
  }, [customerID, getTransactions, getAccessTokenSilently, limit, sortParamValue, page]);

  useEffect(() => {
    getCurrentTeam(getAccessTokenSilently);
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page, getCurrentTeam, getAccessTokenSilently]);

  const dataSource: DataSourceItem<Transaction>[] = transactions.map(transaction => ({
    key: transaction.id.toString(),
    transaction_id: transaction.psp_reference,
    client: transaction.customer_name,
    entity: transaction.customer_type,
    amount: formatCurrency(transaction.total_amount),
    net_amount: formatCurrency(transaction.net_amount),
    data: transaction
  }));

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      <div className="flex flex-grow flex-col overflow-y-auto">
        {isLoading && transactions.length === 0 ? (
          <LoadingPage />
        ) : (
          <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
        )}
        {!isLoading && transactions.length === 0 && (
          <div className="text-center text-gray-400 mt-12">No transactions found</div>
        )}
      </div>
      <TablePaginationFooter
        limit={limit}
        setLimit={setLimit}
        page={page}
        setPage={setPage}
        totalRecords={transactionsCount}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CustomerDetailTransactionsList;
